<template>
    <ScrollingDocument
      class="pdf-document"
      :class="{'double': doubleEnabled, 'row draggable': isPageOrderable}"
      v-bind="{pages, pdfDocs, pageCount, currentPage, isPageOrderable, documentData}"
      v-slot="{page, index, isPageFocused, isElementFocused, pdfid}"
      :enable-page-jump="true"
      @page-jump="onPageJump"
      >
      <div class="zoom">
        <PDFAnnotation
          :key="pdfid + page._pageInfo.rotate"
          v-bind="{headers, pdfid, scale, fit, optimalScale, page, isPageFocused, isElementFocused, contacts, applicants, isAddingSignature, annotations, isAnnotating, isApplicantSet, documentData, workspace, currentPage, copiedAnnotation, leftClickedArea}"
          @select-applicant="onApplicantSelect">
          <PDFPage
            v-bind="{scale, fit, optimalScale, page, isPageFocused, isElementFocused, pdfid}"
          />
        </PDFAnnotation>

        <button class="pdf-toolbox" v-if="isPageOrderable" v-b-tooltip.hover title="Remove Page" @click="removePage(index)">
          <font-awesome-icon :icon="['fas', 'times-circle']" />
        </button>

      </div>
    </ScrollingDocument>
</template>

<script>
// PDFDocument renders an entire PDF inline using
// PDF.js and <canvas>. Currently does not support,
// rendering of selected pages (but could be easily
// updated to do so).
import {PIXEL_RATIO, VIEWPORT_RATIO} from './utils/constants';

import ScrollingDocument from './ScrollingDocument';
import PDFPage from './PDFPage';
import PDFAnnotation from './PDFAnnotation';

export default {
  name: 'PDFDocument',

  components: {
    ScrollingDocument,
    PDFPage,
    PDFAnnotation
  },

  props: {
    headers: {
      type: Object,
      required: true
    },
    applicants: {
      type: Array
    },
    contacts: {
      type: Array
    },
    pages: {
      required: true,
    },
    pdfDocs: {
      required: false,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    scale: {
      type: Number,
      default: 1.0,
    },
    optimalScale: {
      type: Number,
    },
    fit: {
      type: String,
    },
    currentPage: {
      default: 1,
    },
    isPreviewEnabled: {
      default: false,
    },
    doubleEnabled: {
      default: false,
    },
    isPageOrderable: {
      default: false,
    },
    isAddingSignature: {
      default:false,
    },
    isAnnotating: {
      default:false,
    },
    annotations: {
      default:false,
    },
    isApplicantSet: {
      type: Object
    },
    documentData: {
      type: Object,
      required: true
    },
    workspace: {
      required: false
    }
  },
  data() {
    return {
      copiedAnnotation: null,
      leftClickedArea:null,
    }
  },

  computed: {
    defaultViewport() {
      if (!this.pages.length) return {width: 0, height:0};
      const [page] = this.pages.map(doc => doc.page);
      return page.getViewport(1.0);
    },

    isPortrait() {
      const {width, height} = this.defaultViewport;
      return width <= height;
    },
  },

  methods: {
    pageWidthScale() {
      const {defaultViewport, $el} = this;
      if (!defaultViewport.width) return 0;

      return ($el.clientWidth * PIXEL_RATIO) * VIEWPORT_RATIO / defaultViewport.width;
    },

    pageHeightScale() {
      const {defaultViewport, $el} = this;
      if (!defaultViewport.height) return 0;

      return ($el.clientHeight * PIXEL_RATIO) * VIEWPORT_RATIO / defaultViewport.height;
    },

    // Determine an ideal scale using viewport of document's first page, the pixel ratio from the browser
    // and a subjective scale factor based on the screen size.
    fitWidth() {
      const scale = this.pageWidthScale();
      this.updateScale(scale, {isOptimal: !this.optimalScale});
    },

    fitHeight() {
      const scale = this.isPortrait ? this.pageHeightScale() : this.pageWidthScale();
      this.updateScale(scale);
    },

    fitAuto() {
      this.fitWidth();
      const scale = Math.min(this.pageWidthScale(), this.pageHeightScale());
      this.updateScale(scale);
    },

    setFixedWidth() {
      this.$bus.$emit('pdf::pages::visibility::zoom','fixed');
    },

    updateScale(scale, {isOptimal = false} = {}) {
      if (!scale) return;
      this.$bus.$emit('pdf::pages::visibility::scale', {scale, isOptimal});
    },

    onPageJump(scrollTop) {
      this.$el.scrollTop = scrollTop; // triggers 'scroll' event
    },

    onApplicantSelect(payload) {
      this.$emit('select-applicant', payload);
    },

    addSignature(payload) {
      this.$emit('add-signature', payload);
    },

    removeSignature(payload) {
      this.$emit('remove-signature', payload);
    },

    removePage(index) {
      this.$bus.$emit('pdf::page::remove', index);
    },
  },

  created() {
    this.$bus.$on('pdf::pages::reset', this.fitAuto);
    this.$bus.$on('pdf::annotation::copy', data => {
      this.copiedAnnotation = data;
    });
    this.$bus.$on("pdf::annotation::left::click", payload => { this.leftClickedArea = payload; });
  },

  watch: {
    pageMoving() {
    },
    fit(fit) {
      switch (fit) {
        case 'width':
          this.fitWidth();
          break;

        case 'auto':
          this.fitAuto();
          break;

        case 'fixed':
          this.setFixedWidth();
          break;

        default:
          break;
      }
    },
    pageCount: 'fitAuto',
    isPreviewEnabled: 'fitAuto'
  },
};
</script>

<style  lang="scss" >
.pdf-document {
  position: absolute;
  overflow: auto;
  width: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: #f5f5f5;
}

.zoom {
  transform: scale(1);
  transform-origin: 0% 0%;
}

.pdf-toolbox {
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  z-index: 100;
  font-size: 20px;
  line-height: 0px;
  padding: 0px;
  margin: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  background: #000;
  color: #fff;
  border: 0;
}

.scrolling-page {
  margin-bottom: 1em;
}

.scrolling-page canvas {
  height: auto!important;
}

.double .scrolling-page {
  width: 50%!important;
  padding: 2.5rem;
  float: left;
  margin: 0 auto;
}

.double .scrolling-page canvas {
  height: auto!important;
}

@media print {
  .pdf-document {
    position: static;
  }
}
</style>
